import * as React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AlertBanner, Combobox, Modal, Skeleton, Textarea, useLocale } from '@ministry/ui/internal';

import { listValidNextHolders, recordMovement, type RecordMovementInput } from '../lib/workflow.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { generateIdempotencyKey } from '../lib/idempotency.js';
import { useT } from '../i18n/strings.js';

export type MovementActionKind = 'forward' | 'approve' | 'reject' | 'comment';

export interface MovementActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileId: string;
  action: MovementActionKind;
  expectedVersion: number;
  expectedHolderUserId: string | null;
  /** Called after a successful write — the parent re-fetches the file and its timeline. */
  onRecorded: () => void;
  /** Called when the server reports the file changed since it was read — the parent must reload the file before this action can be retried at all. */
  onStaleConflict: () => void;
}

/**
 * SCREEN_SPECS.md I9. "Never build your own user picker" — `valid-next-holders` IS the
 * authorization answer, only fetched (and only shown) for `forward`.
 *
 * The modal cannot be dismissed while the write is in flight — `Modal`'s own `loading`
 * prop disables both Cancel and Confirm, matching "the single highest-consequence write
 * in the system" framing this screen's spec calls out by name.
 */
export function MovementActionModal({
  open,
  onOpenChange,
  fileId,
  action,
  expectedVersion,
  expectedHolderUserId,
  onRecorded,
  onStaleConflict,
}: MovementActionModalProps): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const [targetUserId, setTargetUserId] = React.useState<string | undefined>(undefined);
  const [remarks, setRemarks] = React.useState('');
  /**
   * R-035: minted ONCE when the modal opens (the user's intent begins here), then reused
   * for every retry of THIS SAME intent — including the retry the user triggers after a
   * network failure. A fresh key is minted only when the modal opens again for a new
   * intent (open transitions false -> true), never on each submit attempt, so a
   * double-click or a dropped-connection retry replays the same key and the server's
   * idempotency gate (367c484) actually sees the repeat.
   */
  const idempotencyKeyRef = React.useRef<string>(generateIdempotencyKey());

  React.useEffect(() => {
    if (open) {
      setTargetUserId(undefined);
      setRemarks('');
      idempotencyKeyRef.current = generateIdempotencyKey();
    }
  }, [open, action]);

  const holdersQuery = useQuery({
    queryKey: ['valid-next-holders', fileId],
    queryFn: ({ signal }) => listValidNextHolders(fileId, signal),
    enabled: open && action === 'forward',
  });

  const mutation = useMutation({
    mutationFn: (input: RecordMovementInput) => recordMovement(fileId, input, idempotencyKeyRef.current),
    onSuccess: () => {
      onOpenChange(false);
      onRecorded();
    },
  });

  const remarksRequired = action === 'reject';
  const remarksMissing = remarksRequired && remarks.trim() === '';
  const targetMissing = action === 'forward' && !targetUserId;

  const submit = () => {
    if (remarksMissing || targetMissing) return;
    const input: RecordMovementInput = {
      action,
      expectedVersion,
      expectedHolderUserId,
      ...(action === 'forward' && targetUserId ? { toUserId: targetUserId } : {}),
      ...(remarks.trim() !== '' ? { remarks: remarks.trim() } : {}),
    };
    mutation.mutate(input);
  };

  const error = mutation.error;
  let errorBanner: React.ReactNode = null;
  let isStaleConflict = false;

  if (error instanceof ApiError) {
    if (error.code === 'CONFLICT_HOLDER_CHANGED') {
      isStaleConflict = true;
      const details = error.details ?? {};
      const statusCode = typeof details.statusCode === 'string' ? details.statusCode : '—';
      const holderUserId = typeof details.holderUserId === 'string' ? details.holderUserId : null;
      // Same defect class as the File Register's "Assigned To" column (UX_REVIEW.md):
      // this endpoint gives no display name for a holder who isn't the caller, so a raw
      // uuid must never be interpolated into user-facing copy here either.
      const holder =
        holderUserId === null
          ? t('common.unassigned')
          : holderUserId === expectedHolderUserId
            ? t('common.you')
            : t('movement.error.conflict.holderOther');
      errorBanner = (
        <AlertBanner variant="critical" title={t('movement.error.conflict.title')}>
          {t('movement.error.conflict.body', { status: statusCode, holder })}
        </AlertBanner>
      );
    } else if (error.code === 'FORBIDDEN_OWNERSHIP') {
      errorBanner = <AlertBanner variant="critical" title={t('movement.error.ownership.title')}>{t('movement.error.ownership.body')}</AlertBanner>;
    } else if (error.code === 'CONFLICT_STATE') {
      errorBanner = <AlertBanner variant="critical" title={t('movement.error.state.title')}>{t('movement.error.state.body')}</AlertBanner>;
    } else if (error.code === 'VALIDATION_FAILED' && error.fieldErrors.some((fe) => fe.field === 'toUserId')) {
      errorBanner = <AlertBanner variant="critical" title={t('movement.error.target.title')}>{t('movement.error.target.body')}</AlertBanner>;
    } else if (error.retryable) {
      errorBanner = <AlertBanner variant="critical" title={t('movement.error.network')} />;
    } else {
      errorBanner = <AlertBanner variant="critical" title={t('movement.error.generic')} />;
    }
  } else if (error instanceof NetworkError) {
    errorBanner = <AlertBanner variant="critical" title={t('movement.error.network')} />;
  }

  const holderOptions = (holdersQuery.data ?? []).map((candidate) => ({
    value: candidate.userId,
    label: `${candidate.fullName} — ${locale === 'ne' ? candidate.orgUnit.nameNe : candidate.orgUnit.nameEn}${
      candidate.onLeave ? t('movement.forwardTarget.onLeave') : ''
    }`,
  }));

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return; // no backdrop/Escape dismissal mid-write (SCREEN_SPECS.md I9)
        onOpenChange(next);
      }}
      title={t(`movement.title.${action}`)}
      description={t(`movement.description.${action}`)}
      confirmLabel={mutation.isPending ? t('movement.submitting') : t('movement.submit')}
      cancelLabel={t('movement.cancel')}
      variant={action === 'reject' ? 'destructive' : 'default'}
      loading={mutation.isPending}
      confirmDisabled={remarksMissing || targetMissing}
      onConfirm={isStaleConflict ? undefined : submit}
    >
      <div className="flex flex-col gap-4">
        {errorBanner}
        {isStaleConflict ? (
          <button
            type="button"
            className="self-start text-sm font-semibold text-text-link underline"
            onClick={() => {
              onOpenChange(false);
              onStaleConflict();
            }}
          >
            {t('movement.error.staleReload')}
          </button>
        ) : null}

        {action === 'forward' ? (
          holdersQuery.isLoading ? (
            <Skeleton shape="list-row" count={2} />
          ) : holdersQuery.isError ? (
            <AlertBanner variant="warning" title={t('movement.forwardTarget.empty')} />
          ) : holderOptions.length === 0 ? (
            <AlertBanner variant="warning" title={t('movement.forwardTarget.empty')} />
          ) : (
            <Combobox
              id="movement-forward-target"
              label={t('movement.forwardTarget.label')}
              placeholder={t('movement.forwardTarget.placeholder')}
              helperText={t('movement.forwardTarget.helper')}
              options={holderOptions}
              value={targetUserId}
              onValueChange={setTargetUserId}
              required
            />
          )
        ) : null}

        <Textarea
          id="movement-remarks"
          label={t('movement.remarks.label')}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          required={remarksRequired}
          errorText={remarksMissing ? t('movement.remarks.required') : undefined}
          helperText={remarksMissing ? undefined : remarksRequired ? t('movement.remarks.requiredHelper') : t('movement.remarks.optionalHelper')}
        />
      </div>
    </Modal>
  );
}
